import React from "react";
import { Box, TextField } from "@mui/material";
import { Dayjs } from "dayjs";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { DesignationType } from "common/types/comparableFields.types";
import { LocalizedDatepicker } from "../../components/form/LocalizedDatepicker";
import { MultiSelect } from "../../components/form/MultiSelect";
import { getDesignationTypeTranslation } from "../../helpers/comparableFields.helpers";
import { MatchRatePolygonMap } from "./MatchRatePolygonMap";
import { LatLng } from "../../types/matchRate.types";

export type MatchRateFilterForm = {
  soldDateFrom: Dayjs | null;
  soldDateTo: Dayjs | null;
  designationTypeIds: number[];
  areaFrom: string;
  areaTo: string;
  constructionYearFrom: string;
  constructionYearTo: string;
  polygon?: LatLng[];
};

interface MatchRateFiltersBarProps {
  filters: MatchRateFilterForm;
  onChange: (filters: MatchRateFilterForm) => void;
}

const fieldSx = { minWidth: 180, flex: "1 1 180px" };

export const MatchRateFiltersBar: React.FC<MatchRateFiltersBarProps> = ({
  filters,
  onChange,
}) => {
  const { t } = useTranslation();
  const designationOptions = Object.values(DesignationType).map((type) => ({
    label: getDesignationTypeTranslation(type),
    value: Number(type),
  }));

  const update = (patch: Partial<MatchRateFilterForm>) => {
    const next = { ...filters, ...patch };
    if (_.isEqual(next, filters)) {
      return;
    }
    onChange(next);
  };

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <LocalizedDatepicker
          label={t("matchRate.soldDateFrom")}
          value={filters.soldDateFrom}
          onChange={(value) => update({ soldDateFrom: value })}
          sx={fieldSx}
          slotProps={{ field: { clearable: true } }}
        />
        <LocalizedDatepicker
          label={t("matchRate.soldDateTo")}
          value={filters.soldDateTo}
          onChange={(value) => update({ soldDateTo: value })}
          sx={fieldSx}
          slotProps={{ field: { clearable: true } }}
        />
        <TextField
          label={t("matchRate.areaFrom")}
          type="number"
          value={filters.areaFrom}
          onChange={(event) => update({ areaFrom: event.target.value })}
          sx={fieldSx}
        />
        <TextField
          label={t("matchRate.areaTo")}
          type="number"
          value={filters.areaTo}
          onChange={(event) => update({ areaTo: event.target.value })}
          sx={fieldSx}
        />
        <TextField
          label={t("matchRate.constructionYearFrom")}
          type="number"
          value={filters.constructionYearFrom}
          onChange={(event) =>
            update({ constructionYearFrom: event.target.value })
          }
          sx={fieldSx}
        />
        <TextField
          label={t("matchRate.constructionYearTo")}
          type="number"
          value={filters.constructionYearTo}
          onChange={(event) =>
            update({ constructionYearTo: event.target.value })
          }
          sx={fieldSx}
        />
      </Box>
      <div style={{ marginBottom: "20px", maxWidth: 520 }}>
        <div style={{ marginBottom: "8px" }}>
          {t("matchRate.designationTypes")}
        </div>
        <MultiSelect
          values={designationOptions}
          selectedValues={filters.designationTypeIds}
          onSelect={(values) => update({ designationTypeIds: values })}
        />
      </div>
      <MatchRatePolygonMap
        polygon={filters.polygon}
        onChange={(polygon) => update({ polygon })}
      />
    </div>
  );
};
